import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  FileText,
  Package,
  Receipt,
  Settings as SettingsIcon,
  ShoppingCart,
  Users,
} from 'lucide-react';

import Brand from '../components/Brand';
import { useAuthStore } from '../store/auth';

// XENERGY Mobile — Home.
//
// Header keeps the Brand-on-canvas splash style; sub-pages use the solid
// amber app-header. This visual asymmetry signals "you're at the dashboard"
// vs "you're inside a domain".
//
// Tiles consolidate to one entry point per domain (Sales / Quotes / Stock /
// Expenses). Each list page handles both browse and create; Sales and Quotes
// list pages have a "+" FAB for the create flow. Capture Expense remains the
// landing for the Expenses tile because in the field, capture is the most
// rapid-fire action and the screenshot reference confirmed this.
//
// A flash message from the previous page (e.g. "Sale saved as draft") shows
// briefly at the top, then auto-hides after 4s.

const TILES = [
  {
    label: 'Sales',
    icon: ShoppingCart,
    hint: 'Invoices · proforma · view',
    to: '/sales',
    iconBg: 'bg-blue-50',
    iconFg: 'text-blue-700',
  },
  {
    label: 'Quotes',
    icon: FileText,
    hint: 'Track approval status',
    to: '/quotes',
    iconBg: 'bg-indigo-50',
    iconFg: 'text-indigo-700',
  },
  {
    label: 'Stock',
    icon: Package,
    hint: 'Check what’s on hand',
    to: '/stock',
    iconBg: 'bg-green-50',
    iconFg: 'text-green-700',
  },
  {
    label: 'Expenses',
    icon: Receipt,
    hint: 'Capture · receipts · ZIMRA',
    to: '/expenses/capture',
    iconBg: 'bg-orange-50',
    iconFg: 'text-orange-700',
  },
  {
    label: 'Customers',
    icon: Users,
    hint: 'Look up · add new',
    to: '/customers',
    iconBg: 'bg-amber-50',
    iconFg: 'text-amber-700',
  },
];

export default function Home() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const [flash, setFlash] = useState(location.state?.flash || null);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  const firstName = user?.name?.split(' ')[0];

  return (
    <main className="min-h-full px-4 pt-safe pb-6">
      <header className="pt-6 pb-5 flex items-start justify-between">
        <div className="min-w-0">
          <Brand size="md" variant="light" subtitle="ERP Mobile" />
          <p className="mt-3 text-small text-gray-600">
            {firstName ? `Hi, ${firstName}` : 'Field ops'}
            <span className="text-gray-400"> · sales, quotes, stock, expenses</span>
          </p>
        </div>
        <Link
          to="/settings"
          aria-label="Settings"
          className="min-h-touch min-w-touch -mr-2 -mt-1 flex items-center justify-center text-gray-600 hover:text-gray-900 rounded-card"
        >
          <SettingsIcon className="h-6 w-6" />
        </Link>
      </header>

      {flash && (
        <div
          role="status"
          className="mb-4 mb-card bg-green-50 border-green-200 text-small text-green-900"
        >
          {flash}
        </div>
      )}

      <section
        aria-label="Quick actions"
        className="grid grid-cols-2 gap-3"
      >
        {TILES.map(({ label, icon: Icon, hint, to, iconBg, iconFg }) => (
          <Link
            key={label}
            to={to}
            className="mb-card-tap flex flex-col items-start gap-2 min-h-touch text-left no-underline"
          >
            <span
              className={`w-10 h-10 rounded-card ${iconBg} flex items-center justify-center`}
              aria-hidden="true"
            >
              <Icon className={`h-5 w-5 ${iconFg}`} />
            </span>
            <span className="text-body font-semibold text-gray-900">{label}</span>
            <span className="text-caption text-gray-500">{hint}</span>
          </Link>
        ))}
      </section>
    </main>
  );
}
